import cn from 'classnames';
import { useStore } from 'effector-react';
import { useEffect, useMemo, useState } from 'react';
import { piiFilterStore } from 'components/PiiGlobalFilterV2/model';
import Preloader from 'components/Preloader';
import { Typo } from 'components/typography/Typo';
import { AssetGroup } from 'models/assetsGroups/dto';
import { getDataFlowDetails } from 'models/assetsGroupsDataFlowDetails/api';
import {
	DataFlowDetailsParams,
	DataFlowDetailsTable,
} from 'models/assetsGroupsDataFlowDetails/dto';
import { TDataDirectionType } from 'models/common/dto';
import { dataTypesById as dtById, dataTypesLoaded } from 'models/dataTypes/store';
import DataFlowStreamFlow from 'views/common/DataFlowStreamFlow';
import DataFlowDataTypeFilter from './DataFlowDataTypeFilter';
import DataFlowDetailsHeader from './DataFlowDetailsHeader';
import { DataFlowDetailsList } from './DataFlowDetailsList';
import styles from './index.module.css';

type Props = {
	group1: AssetGroup['id'];
	group2: AssetGroup['id'];
	direction: TDataDirectionType;
};

const OFFSET_STEP = 100;
const OVER_LIMIT = 101;

export const DataFlowDetails = ({ group1, group2, direction }: Props) => {
	const [isFirstLoading, setFirstLoading] = useState(true);
	const [isLoading, setLoading] = useState(true);
	const [detailsData, setDetailsData] = useState<DataFlowDetailsTable | null>(null);
	const dtIsLoaded = useStore(dataTypesLoaded);
	const [offset, setOffset] = useState(0);
	const [moreDataLoading, setMoreDataLoading] = useState(false);

	const [flowDirection, setFlowDirection] =
		useState<DataFlowDetailsParams['flowDirection']>('outgoing');
	const [streamDirection, setStreamDirection] =
		useState<DataFlowDetailsParams['streamDirection']>('');

	const dataTypesMap = useStore(dtById); // For map from string to number
	const { dataTypes: dataTypesStore } = useStore(piiFilterStore); // Get dataType by string aka 'PERSON'
	// Parse to the array of ids
	const dataTypesById = useMemo(
		() => dataTypesStore.map((dt) => dataTypesMap[dt].id),
		[dataTypesStore]
	);
	const [activeDataTypes, setActiveDataTypes] =
		useState<DataFlowDetailsTable['data_types']>(dataTypesById);

	// Reset settings when we click to the other service on the table
	useEffect(() => {
		setFirstLoading(true);
		setActiveDataTypes(dataTypesById);
		setFlowDirection('outgoing');
		setStreamDirection('');
		setOffset(0);
	}, [group2]);

	// Update activeDataType when the global filter was updated
	useEffect(() => {
		setActiveDataTypes(dataTypesById);
	}, [dataTypesStore]);

	// Reset offset because changing of filter or asset2
	useEffect(() => {
		setOffset(0);
	}, [group2, activeDataTypes, flowDirection, streamDirection]);

	useEffect(() => {
		if (!group2 || !dtIsLoaded) return;

		setOffset(0);
		setLoading(true);

		loadData().then((resp) => {
			setDetailsData(resp);
			setLoading(false);
			setFirstLoading(false);
		});
	}, [group1, group2, dtIsLoaded, activeDataTypes, flowDirection, streamDirection]);

	function loadData(_offset = 0) {
		return getDataFlowDetails({
			group1Id: group1,
			group2Id: group2,
			dataTypes: activeDataTypes,
			flowDirection,
			streamDirection,
			offset: _offset,
			limit: OVER_LIMIT,
		});
	}

	function loadMoreData() {
		const newOffset = offset + OFFSET_STEP;

		setOffset(newOffset);
		setMoreDataLoading(true);

		loadData(newOffset).then((resp) => {
			resp.streams = detailsData?.streams.concat(resp.streams) || [];

			setDetailsData(resp);
			setMoreDataLoading(false);
		});
	}

	let error = null;
	if (detailsData) {
		if (!detailsData['group1_data']) {
			error = `Service group with id=${group1} not found.`;
		} else if (!detailsData['group2_data']) {
			error = `Service group with id=${group2} not found.`;
		}
	}

	return (
		<Preloader isLoading={isFirstLoading}>
			<div className={styles.container}>
				{detailsData &&
					(error ? (
						<Typo variant="D/Regular/Body-S" color="secondary" className={styles.error}>
							{error}
						</Typo>
					) : (
						<>
							<div className={styles.filters}>
								<DataFlowDetailsHeader
									group1={detailsData['group1_data']}
									group2={detailsData['group2_data']}
									flowDirection={flowDirection}
									direction={direction}
									changeDirection={setFlowDirection}
									disabled={moreDataLoading || isLoading}
								/>

								<div className={cn({ [styles.disabled]: moreDataLoading || isLoading })}>
									<div className={styles.dataTypeFilter}>
										<DataFlowDataTypeFilter
											activeDataTypes={activeDataTypes}
											dataTypes={detailsData.data_types}
											onChange={setActiveDataTypes}
										/>
									</div>

									<DataFlowStreamFlow direction={streamDirection} onChange={setStreamDirection} />
								</div>
							</div>

							<Preloader isLoading={isLoading} className={styles.table}>
								<DataFlowDetailsList
									activeDataTypes={activeDataTypes}
									loadMoreData={loadMoreData}
									moreData={detailsData['more_data']}
									moreDataLoading={moreDataLoading}
									streams={detailsData.streams}
								/>
							</Preloader>
						</>
					))}
			</div>
		</Preloader>
	);
};
